import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, toApiError, unwrap, type ApiError, type Envelope } from '@/lib/apiClient';
import type { ShopStatusDTO } from '@/types/api';

const KEY = ['shop-status'];

async function fetchStatus(): Promise<ShopStatusDTO> {
  const res = await api.get<Envelope<ShopStatusDTO>>('/api/v1/shop/status');
  return unwrap(res);
}

/**
 * Current order-acceptance state for the logged-in cashier's shop. Polled on a
 * slow interval so a timed pause flips back to "accepting" in the UI without a
 * manual refresh, and so a pause set on another device shows up here too.
 */
export function useShopStatus(enabled = true) {
  return useQuery({
    queryKey: KEY,
    queryFn: fetchStatus,
    enabled,
    staleTime: 15_000,
    refetchInterval: 60_000,
  });
}

export interface PauseVars {
  durationMinutes: number | null; // null = indefinite
  reason?: string | null;
}

export function usePauseShop() {
  const qc = useQueryClient();
  return useMutation<ShopStatusDTO, ApiError, PauseVars>({
    mutationFn: async ({ durationMinutes, reason }) => {
      try {
        const res = await api.post<Envelope<ShopStatusDTO>>('/api/v1/shop/pause', {
          durationMinutes,
          reason: reason ?? null,
        });
        return unwrap(res);
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}

export function useResumeShop() {
  const qc = useQueryClient();
  return useMutation<ShopStatusDTO, ApiError, void>({
    mutationFn: async () => {
      try {
        const res = await api.post<Envelope<ShopStatusDTO>>('/api/v1/shop/resume');
        return unwrap(res);
      } catch (err) {
        throw toApiError(err);
      }
    },
    onSuccess: (data) => qc.setQueryData(KEY, data),
  });
}
